import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import BottomNav from './components/BottomNav';
import ErrorBoundary from './components/ErrorBoundary';
import { Box, Typography, Button } from '@mui/material';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TwoFactorAuth from './pages/TwoFactorAuth';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AccountDetails from './pages/AccountDetails';
import ChangePassword from './pages/ChangePassword';
import Notifications from './pages/Notifications';
import Support from './pages/Support';
import Settings from './pages/Settings';
import Savings from './pages/Savings';
import MapRoutesMapLibre from './pages/MapRoutesMapLibre';
import Drivers from './pages/Drivers';
import Cards from './pages/Cards';
import { quikqAuthService } from './services/quikqAuth';
import { logger, LogCategory } from './services/logger';
import './utils/userRoleTest'; // Import test utility

// const Profile = () => <Box sx={{ p: 2 }}>Profile</Box>;

const navPaths = ['/', '/map', '/savings', '/profile'];

// Auth protection component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  
  React.useEffect(() => {
    // Check authentication status
    const checkAuth = () => {
      const authenticated = quikqAuthService.isAuthenticated();
      setIsAuthenticated(authenticated);
      setIsChecking(false);
      
      if (!authenticated) {
        navigate('/login', { replace: true });
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  if (isChecking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return null;
  }
  
  return <>{children}</>;
};

// Driver-only route protection
const DriverRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isDriver = quikqAuthService.isDriver();
  
  if (!isDriver) {
    return <Box sx={{ p: 2, textAlign: 'center' }}>Access denied. This page is only for drivers.</Box>;
  }
  
  return <>{children}</>;
};

// User-only route protection (not drivers)
const UserRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isUser = quikqAuthService.isUser();
  
  if (!isUser) {
    return <Box sx={{ p: 2, textAlign: 'center' }}>Access denied. This page is only for regular users.</Box>;
  }
  
  return <>{children}</>;
};

function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathnameRef = React.useRef<string>('');
  
  // Check if user is a driver and redirect to map if they try to access other pages
  useEffect(() => {
    const isDriver = quikqAuthService.isDriver();
    const isAuthenticated = quikqAuthService.isAuthenticated();
    
    if (isAuthenticated && isDriver) {
      // If driver tries to access any page other than map or support, redirect to map
      if (location.pathname !== '/map' && 
          location.pathname !== '/support' &&
          location.pathname !== '/profile/support' &&
          location.pathname !== '/login' && 
          location.pathname !== '/register' &&
          location.pathname !== '/forgot-password' &&
          !location.pathname.startsWith('/reset-password/') &&
          location.pathname !== '/two-factor-auth') {
        navigate('/map');
      }
    }
  }, [location.pathname, navigate]);

  // Log page navigation
  useEffect(() => {
    const isAuthenticated = quikqAuthService.isAuthenticated();
    if (isAuthenticated && location.pathname !== prevPathnameRef.current) {
      const currentUser = quikqAuthService.getCurrentUser();
      logger.info(LogCategory.NAVIGATION, `Page navigation: ${prevPathnameRef.current || '(initial)'} -> ${location.pathname}`, {
        from: prevPathnameRef.current || '(initial)',
        to: location.pathname,
        userId: currentUser?.userId,
        role: currentUser?.role
      });
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);
  
  // Determine current tab based on pathname
  const getCurrentTab = (pathname: string): number => {
    // For drivers, determine tab based on current route
    if (quikqAuthService.isDriver()) {
      if (pathname === '/map') return 0; // Map tab
      if (pathname === '/support' || pathname === '/profile/support') return 1; // Support tab
      return 0; // Default to map
    }
    
    // Direct matches
    if (pathname === '/') return 0;
    if (pathname === '/map') return 1;
    if (pathname === '/savings') return 2;
    if (pathname === '/profile') return 3;
    
    // Profile section pages - all should show Profile tab as active
    if (pathname === '/cards' || 
        pathname === '/drivers' || 
        pathname === '/notifications' || 
        pathname === '/profile/support' || 
        pathname === '/profile/settings' ||
        pathname === '/account-details' ||
        pathname === '/change-password' ||
        pathname === '/two-factor-auth') {
      return 3; // Profile tab
    }
    
    // Profile sub-pages (if they exist)
    if (pathname.startsWith('/profile/')) {
      return 3; // Profile tab
    }
    
    // Default to Dashboard
    return 0;
  };
  
  const currentTab = getCurrentTab(location.pathname);
  const isAuthenticated = quikqAuthService.isAuthenticated();
  const isMapPage = location.pathname === '/map';

  // Ne prikazuj BottomNav na login/register rutama ili ako korisnik nije ulogovan
  const hideNav = location.pathname === '/login' || 
                  location.pathname === '/register' ||
                  !isAuthenticated;

  // Prevent body scroll when on map page and enable full width
  React.useEffect(() => {
    const rootElement = document.getElementById('root');
    if (isMapPage) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (rootElement) {
        rootElement.classList.add('map-page-full-width');
      }
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (rootElement) {
        rootElement.classList.remove('map-page-full-width');
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (rootElement) {
        rootElement.classList.remove('map-page-full-width');
      }
    };
  }, [isMapPage]);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      pb: hideNav ? 0 : { xs: '56px', sm: '64px', md: '72px' }, // Add padding for floating nav
      overflow: isMapPage ? 'hidden' : 'visible',
      width: isMapPage ? '100%' : '100%',
      maxWidth: isMapPage ? '100%' : 'none'
    }}>
      <Box sx={{ 
        flex: 1, 
        overflow: isMapPage ? 'hidden' : 'auto',
        height: isMapPage ? '100vh' : 'auto',
        width: '100%',
        maxWidth: isMapPage ? '100%' : 'none',
        position: isMapPage ? 'relative' : 'static'
      }}>
        <Routes>
        <Route path="/" element={
          <ProtectedRoute>
            <UserRoute>
              <Dashboard />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/map" element={
          <ProtectedRoute>
            <MapRoutesMapLibre />
          </ProtectedRoute>
        } />
        <Route path="/savings" element={
          <ProtectedRoute>
            <UserRoute>
              <Savings />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <UserRoute>
              <Profile />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/profile/account-details" element={
          <ProtectedRoute>
            <UserRoute>
              <AccountDetails />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/profile/change-password" element={
          <ProtectedRoute>
            <UserRoute>
              <ChangePassword />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/profile/notifications" element={
          <ProtectedRoute>
            <UserRoute>
              <Notifications />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/profile/settings" element={
          <ProtectedRoute>
            <UserRoute>
              <Settings />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/profile/support" element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        } />
        <Route path="/drivers" element={
          <ProtectedRoute>
            <UserRoute>
              <Drivers />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/cards" element={
          <ProtectedRoute>
            <UserRoute>
              <Cards />
            </UserRoute>
          </ProtectedRoute>
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/two-factor-auth" element={<TwoFactorAuth />} />
        {/* Catch-all route for 404 */}
        <Route path="*" element={
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>404 - Page Not Found</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The page you're looking for doesn't exist.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Box>
        } />
        </Routes>
      </Box>
      {!hideNav && (
        <Box sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1100,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15), 0 -2px 8px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease-in-out'
        }}>
          <BottomNav 
            value={currentTab} 
            onChange={(_, v) => {
              // Only allow navigation for regular users, not drivers
              if (!quikqAuthService.isDriver()) {
                navigate(navPaths[v]);
              }
            }} 
          />
        </Box>
      )}
    </Box>
  );
}

function App() {
  useEffect(() => {
    // Resume the background token-refresh loop on cold app loads so the QuikQ
    // session stays alive across page reloads (and not only after a fresh login).
    if (quikqAuthService.isAuthenticated()) {
      quikqAuthService.startTokenRefreshTimer();
    }
    // Best-effort: drop stations-cache entries that crossed the 9 AM / 5 PM
    // Chicago refresh boundary, so the IndexedDB doesn't accumulate forever.
    void import('./services/stationsCache').then((m) => m.pruneStaleCacheEntries());
    return () => {
      quikqAuthService.stopTokenRefreshTimer();
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <MainLayout />
      </Router>
    </ErrorBoundary>
  );
}

export default App;
